/**
 * CL-PRODUCT-SELECTIONS-003 — Admin Products & Selections workspace (pure).
 */

import {
  PRODUCT_OPTION_STATUS,
  PRODUCT_REQUIREMENT_RECORD_STATUS,
  PRODUCT_REQUIREMENT_WORKFLOW_STATUS,
  normalizeProductSpecEntry,
  normalizeProductSpecList,
} from "./projectProductSelectionFoundationModel";
import {
  ADMIN_PRODUCT_OPTION_DECISION_OPTIONS,
  PRODUCT_OPTION_DECISION_STATUS,
  PRODUCT_OPTION_RECORD_LIFECYCLE,
  normalizeProductOptionDecisionFields,
  productOptionDecisionStatusLabel,
} from "./projectProductOptionDecisionModel";
import {
  resolveNextProductOptionSortOrder,
  sortProductOptionsByOrder,
} from "./projectProductOptionOrderModel";

export const ADMIN_PRODUCTS_SELECTIONS_TAB_KEY = "products_selections";

export const ADMIN_PRODUCTS_SELECTIONS_TAB_LABEL = "Products & selections";

export const ADMIN_PRODUCT_REQUIREMENT_WORKFLOW_OPTIONS = Object.freeze([
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.IDENTIFIED, label: "Identified" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.RESEARCHING, label: "Researching" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.SAMPLES_REQUESTED, label: "Samples requested" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.SAMPLES_RECEIVED, label: "Samples received" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.OPTIONS_PROPOSED, label: "Options proposed" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.CLIENT_REVIEW, label: "Client review" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.SELECTED, label: "Selected" },
  { value: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.APPROVED, label: "Approved" },
]);

export const ADMIN_PRODUCT_OPTION_STATUS_OPTIONS = Object.freeze([
  { value: PRODUCT_OPTION_STATUS.DRAFT, label: "Draft" },
  { value: PRODUCT_OPTION_STATUS.ACTIVE, label: "Active" },
  { value: PRODUCT_OPTION_STATUS.REJECTED, label: "Rejected" },
  { value: PRODUCT_OPTION_STATUS.SUPERSEDED, label: "Superseded" },
  { value: PRODUCT_OPTION_STATUS.ARCHIVED, label: "Archived" },
]);

export { ADMIN_PRODUCT_OPTION_DECISION_OPTIONS, productOptionDecisionStatusLabel };

function safeStr(value) {
  return String(value ?? "").trim();
}

function normalizeName(value) {
  return safeStr(value).replace(/\s+/g, " ");
}

function normalizeNameKey(value) {
  return normalizeName(value).toLowerCase();
}

function isActiveRecord(row) {
  return safeStr(row?.status || "active").toLowerCase() !== "archived";
}

export function isActiveCanonicalAssignment(assignment = null) {
  if (!assignment || typeof assignment !== "object") return false;
  return safeStr(assignment.status || "active").toLowerCase() === "active";
}

export function resolveAdminProductsSelectionsAccess({
  isGlobalAdmin = false,
  assignment = null,
  permissions = {},
} = {}) {
  const activeAssignment = isActiveCanonicalAssignment(assignment);
  const effectivePermissions =
    (assignment?.permissions && typeof assignment.permissions === "object" ? assignment.permissions : null)
    || (permissions && typeof permissions === "object" ? permissions : {});

  const canView = isGlobalAdmin
    || (activeAssignment && (
      effectivePermissions.canManageProjectSuppliers === true
      || effectivePermissions.canViewProjectSuppliers === true
    ));
  const canManage = isGlobalAdmin
    || (activeAssignment && effectivePermissions.canManageProjectSuppliers === true);

  return {
    canView,
    canManage,
    readOnly: canView && !canManage,
    assignmentActive: activeAssignment,
  };
}

export function shouldShowAdminProductsSelectionsTab(access = {}) {
  return access?.canView === true || access?.canManage === true;
}

export function resolveAuthorizedAdminProductsSelectionsTab({
  requestedTab = "",
  access = {},
  fallbackTab = "overview",
} = {}) {
  const tab = safeStr(requestedTab).toLowerCase();
  if (tab !== ADMIN_PRODUCTS_SELECTIONS_TAB_KEY) return tab || fallbackTab;
  if (!shouldShowAdminProductsSelectionsTab(access)) return fallbackTab;
  return ADMIN_PRODUCTS_SELECTIONS_TAB_KEY;
}

export function findDuplicateActiveName(rows = [], { name = "", excludeId = "" } = {}) {
  const key = normalizeNameKey(name);
  if (!key) return null;
  const excluded = safeStr(excludeId);
  return (Array.isArray(rows) ? rows : []).find((row) => {
    if (!isActiveRecord(row)) return false;
    if (excluded && safeStr(row?.id) === excluded) return false;
    return normalizeNameKey(row?.name) === key;
  }) || null;
}

export function validateCategoryParentReference({
  categoryId = "",
  parentCategoryId = "",
  categories = [],
} = {}) {
  const parentId = safeStr(parentCategoryId);
  if (!parentId) return { ok: true, parentCategoryId: null };
  const selfId = safeStr(categoryId);
  if (selfId && parentId === selfId) {
    return { ok: false, error: "A category cannot be its own parent." };
  }
  const parent = (Array.isArray(categories) ? categories : []).find((row) => safeStr(row?.id) === parentId);
  if (!parent) return { ok: false, error: "Choose a valid parent category." };
  if (selfId) {
    const visited = new Set([selfId]);
    let cursor = parent;
    while (cursor) {
      const cursorId = safeStr(cursor?.id);
      if (!cursorId) break;
      if (visited.has(cursorId)) {
        return { ok: false, error: "Parent category would create a cycle." };
      }
      visited.add(cursorId);
      const nextParentId = safeStr(cursor?.parentCategoryId);
      if (!nextParentId) break;
      cursor = categories.find((row) => safeStr(row?.id) === nextParentId);
    }
  }
  return { ok: true, parentCategoryId: parentId };
}

export function validateUniqueActiveCategoryName({
  name = "",
  categoryId = "",
  categories = [],
} = {}) {
  const duplicate = findDuplicateActiveName(categories, { name, excludeId: categoryId });
  if (duplicate) {
    return { ok: false, error: "An active category with this name already exists." };
  }
  return { ok: true };
}

export function validateUniqueActiveAreaName({
  name = "",
  areaId = "",
  areas = [],
} = {}) {
  const duplicate = findDuplicateActiveName(areas, { name, excludeId: areaId });
  if (duplicate) {
    return { ok: false, error: "An active area with this name already exists." };
  }
  return { ok: true };
}

export function sortProductCategories(categories = []) {
  return [...(Array.isArray(categories) ? categories : [])].sort((a, b) => {
    const order = Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0);
    if (order !== 0) return order;
    return normalizeName(a?.name).localeCompare(normalizeName(b?.name));
  });
}

export function sortProductAreas(areas = []) {
  return sortProductCategories(areas);
}

export function sortProductRequirements(requirements = []) {
  return [...(Array.isArray(requirements) ? requirements : [])].sort((a, b) => {
    const order = Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0);
    if (order !== 0) return order;
    return safeStr(a?.title).localeCompare(safeStr(b?.title));
  });
}

export function sortProductOptions(options = [], { approvedOptionId = "" } = {}) {
  return sortProductOptionsByOrder(options, { approvedOptionId });
}

export function resolveNextProductOptionSortOrderForRequirement(options = []) {
  return resolveNextProductOptionSortOrder(options);
}

export function finalizeProductOptionRecord(record = {}, {
  existing = null,
  approvedOptionId = "",
} = {}) {
  const merged = {
    ...(existing || {}),
    ...(record || {}),
    id: existing?.id,
  };
  const decisionFields = normalizeProductOptionDecisionFields(merged, {
    approvedOptionId,
    preserveArchivedDecision: safeStr(record?.recordLifecycle || existing?.recordLifecycle)
      === PRODUCT_OPTION_RECORD_LIFECYCLE.ARCHIVED,
  });
  if (!decisionFields.ok) return decisionFields;
  return {
    ok: true,
    record: {
      ...record,
      optionStatus: decisionFields.optionStatus,
      recordLifecycle: decisionFields.recordLifecycle,
      decisionStatus: decisionFields.decisionStatus,
      isShortlisted: decisionFields.isShortlisted,
      ...(decisionFields.decisionStatusBeforeArchive
        ? { decisionStatusBeforeArchive: decisionFields.decisionStatusBeforeArchive }
        : {}),
    },
  };
}

export function filterProductRequirements(requirements = [], filters = {}) {
  const search = safeStr(filters.search).toLowerCase();
  const categoryId = safeStr(filters.categoryId);
  const areaId = safeStr(filters.areaId);
  const workflowStatus = safeStr(filters.workflowStatus);
  const recordStatus = safeStr(filters.recordStatus || "active").toLowerCase();

  return (Array.isArray(requirements) ? requirements : []).filter((row) => {
    const status = safeStr(row?.status || PRODUCT_REQUIREMENT_RECORD_STATUS.ACTIVE).toLowerCase();
    if (recordStatus === "all") {
      // no record-status filter
    } else if (recordStatus === "active" && status === PRODUCT_REQUIREMENT_RECORD_STATUS.ARCHIVED) return false;
    else if (recordStatus === "archived" && status !== PRODUCT_REQUIREMENT_RECORD_STATUS.ARCHIVED) return false;
    if (categoryId && safeStr(row?.categoryId) !== categoryId) return false;
    if (areaId && safeStr(row?.areaId) !== areaId) return false;
    if (workflowStatus && safeStr(row?.workflowStatus) !== workflowStatus) return false;
    if (search) {
      const haystack = [
        row?.title,
        row?.description,
        row?.clientSummary,
        ...(Array.isArray(row?.tags) ? row.tags : []),
      ]
        .map((part) => safeStr(part).toLowerCase())
        .join(" ");
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

export function buildArchiveStatusPatch(statusConstant, actorUid = "") {
  return {
    status: statusConstant,
    archivedByUid: safeStr(actorUid) || null,
    updatedByUid: safeStr(actorUid) || null,
  };
}

export function buildRestoreActiveStatusPatch(statusConstant, actorUid = "") {
  return {
    status: statusConstant,
    archivedByUid: null,
    updatedByUid: safeStr(actorUid) || null,
  };
}

export function newRequirementDefaults() {
  return {
    workflowStatus: PRODUCT_REQUIREMENT_WORKFLOW_STATUS.IDENTIFIED,
    status: PRODUCT_REQUIREMENT_RECORD_STATUS.ACTIVE,
    clientVisible: false,
    tradeVisible: false,
    sortOrder: 1000,
  };
}

export function newOptionDefaults() {
  return {
    optionStatus: PRODUCT_OPTION_STATUS.ACTIVE,
    recordLifecycle: PRODUCT_OPTION_RECORD_LIFECYCLE.ACTIVE,
    decisionStatus: PRODUCT_OPTION_DECISION_STATUS.OPTION,
    clientVisible: false,
    tradeVisible: false,
    specs: [],
  };
}

export function normalizeSpecificationEditorRows(rows = []) {
  return normalizeProductSpecList(
    (Array.isArray(rows) ? rows : [])
      .map((row) => normalizeProductSpecEntry(row))
      .filter(Boolean)
  );
}

export function moveSpecificationRow(rows = [], fromIndex = 0, direction = -1) {
  const list = [...(Array.isArray(rows) ? rows : [])];
  const toIndex = fromIndex + direction;
  if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) {
    return list;
  }
  const [item] = list.splice(fromIndex, 1);
  list.splice(toIndex, 0, item);
  return list;
}

export function removeSpecificationRow(rows = [], index = 0) {
  const list = [...(Array.isArray(rows) ? rows : [])];
  if (index < 0 || index >= list.length) return list;
  list.splice(index, 1);
  return list;
}

export function resolveCategoryLabel(categoryId = "", categories = []) {
  const id = safeStr(categoryId);
  if (!id) return "";
  const row = (Array.isArray(categories) ? categories : []).find((item) => safeStr(item?.id) === id);
  const name = normalizeName(row?.name);
  if (!name) return id;
  if (!isActiveRecord(row)) return `${name} (archived)`;
  return name;
}

export function resolveAreaLabel(areaId = "", areas = []) {
  const id = safeStr(areaId);
  if (!id) return "";
  const row = (Array.isArray(areas) ? areas : []).find((item) => safeStr(item?.id) === id);
  const name = normalizeName(row?.name);
  if (!name) return id;
  if (!isActiveRecord(row)) return `${name} (archived)`;
  return name;
}

export function workflowStatusLabel(value = "") {
  const key = safeStr(value);
  const match = ADMIN_PRODUCT_REQUIREMENT_WORKFLOW_OPTIONS.find((row) => row.value === key);
  return match?.label || key;
}

const DEFAULT_PRODUCT_SORT_ORDER = 1000;
const PRODUCT_SORT_ORDER_STEP = 10;

export function resolveNextProductSortOrder(rows = [], fallback = DEFAULT_PRODUCT_SORT_ORDER) {
  const values = (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.sortOrder))
    .filter((value) => Number.isFinite(value));
  if (values.length === 0) return fallback;
  return Math.max(...values) + PRODUCT_SORT_ORDER_STEP;
}

export function summarizeSelectionWorkflowCounts(requirements = []) {
  const counts = {
    total: 0,
    active: 0,
    archived: 0,
    identified: 0,
    inProgress: 0,
    clientReview: 0,
    approved: 0,
  };
  (Array.isArray(requirements) ? requirements : []).forEach((row) => {
    counts.total += 1;
    const status = safeStr(row?.status || "active").toLowerCase();
    if (status === "archived") {
      counts.archived += 1;
      return;
    }
    counts.active += 1;
    const workflow = safeStr(row?.workflowStatus).toLowerCase();
    if (workflow === PRODUCT_REQUIREMENT_WORKFLOW_STATUS.APPROVED) counts.approved += 1;
    else if (workflow === PRODUCT_REQUIREMENT_WORKFLOW_STATUS.CLIENT_REVIEW) counts.clientReview += 1;
    else if (
      workflow === PRODUCT_REQUIREMENT_WORKFLOW_STATUS.IDENTIFIED
      || workflow === PRODUCT_REQUIREMENT_WORKFLOW_STATUS.RESEARCHING
    ) counts.identified += 1;
    else counts.inProgress += 1;
  });
  return counts;
}

export function formatProductOptionDisplayName(option = {}) {
  const label = safeStr(option?.label);
  if (label) return label;
  const manufacturer = safeStr(option?.manufacturer);
  const productName = safeStr(option?.productName);
  if (manufacturer && productName) return `${manufacturer} – ${productName}`;
  return manufacturer || productName || "Product";
}

export function mapProductSaveError(error, { recordType = "record", action = "save" } = {}) {
  const code = safeStr(error?.code).toLowerCase();
  const message = safeStr(error?.message);

  if (message.includes("Invalid document reference") || message.includes("odd number of segments")) {
    return `We couldn't save this ${recordType}. Nothing was created. Please try again or report the problem.`;
  }
  if (code === "permission-denied" || message.toLowerCase().includes("permission")) {
    return `You don't have permission to ${action} this ${recordType}.`;
  }
  if (code === "unavailable" || code === "network-request-failed") {
    return "Network error. Check your connection and try again.";
  }
  if (message) {
    return message;
  }
  return `We couldn't save this ${recordType}. Nothing was created. Please try again or report the problem.`;
}
